import {signal} from '@angular/core';
import {isKeyOf, isValueOf} from '@shared-types';

export type SortByMap = StringRecord<string>;

export type IQueryMarkers<SortMap extends SortByMap = SortByMap, SortKey extends keyof SortMap = keyof SortMap> = {
	searchText: string;
	sortBy: SortMap[SortKey];
	sortDesc: boolean;
};
export const defaultQueryMarkers: IQueryMarkers = {
	searchText: '',
	sortBy: '',
	sortDesc: false,
};

export type ExtractSortMap<O> = O extends StringRecord<string> ? O : never;
export type ExtractSortKeys<B> = B extends AbstractQueryBuilder<any, infer M> ? keyof M : never;

export abstract class AbstractQueryBuilder<T extends IQueryMarkers, SortMap extends SortByMap> {
	private readonly initial: T;
	public readonly markers = signal<T>({} as T);
	/**
	 * you can add mappers to the markers and adjust the format of the markers when building the query
	 * default mappers:
	 * - this.markersMapper.set('searchText', (value) => `${value} `); // this marker need to sent as value only
	 */
	public readonly markersMapper: Map<keyof T, (value: T[keyof T]) => string> = new Map([['searchText', value => `${value}`]]);

	/** כל יורש חייב לספק מפה של אפשרויות sortBy */
	protected abstract readonly sortByMap: SortMap;

	protected constructor(initialMarkers: T) {
		this.initial = structuredClone({...defaultQueryMarkers, ...initialMarkers});
		this.markers.set(structuredClone({...defaultQueryMarkers, ...initialMarkers}));
	}

	/** בחירת sortBy לפי מפתח במפה */
	public setSortBy(sortBy: KeyOrValueOf<SortMap>): this {
		if (isKeyOf(this.sortByMap, sortBy)) {
			this.markers.update(m => ({...m, sortBy: this.sortByMap[sortBy]}));
		} else if (isValueOf(this.sortByMap, sortBy)) {
			this.markers.update(m => ({...m, sortBy}));
		} else {
			throw new Error(`Invalid sortBy value: ${sortBy}`);
		}
		return this;
	}

	public setSortDesc(sortDesc: boolean): this {
		this.markers.update(m => ({...m, sortDesc}));
		return this;
	}

	public setSearch(text: string): this {
		this.markers.update(m => ({...m, searchText: text}));
		return this;
	}

	public updateMarkers(updates: Partial<T>): this {
		this.markers.update(m => ({...m, ...updates}));
		return this;
	}

	public reset(): this {
		this.markers.set(structuredClone(this.initial));
		return this;
	}

	public build(): string {
		const fixed = this.buildFixedParts();
		const dynamic = this.formatMarkers(this.markers());
		return [...fixed, ...dynamic].filter(Boolean).join(' ');
	}

	/**
	 * this method is used to format the markers to a string
	 * use case:
	 * - add a marker to the query
	 * e.g. `?searchText=test` will be added to the query
	 * you can override this method to change the format or add/adjust markers
	 */
	protected formatMarkers(m: Partial<T>): string[] {
		return Object.entries(m).flatMap(([key, value]) => {
			if (value === null || value === '' || value === undefined) return [];
			const mapper = this.markersMapper.get(key as keyof T);
			if (mapper) {
				return mapper(value);
			}
			return [`${key}:${value}`];
		});
	}

	/**
	 * this method is used to build the fixed parts of the query
	 * use case:
	 * - add a fixed part to the query
	 * e.g. `?page=1&size=10` will be added to the query
	 * @returns
	 */
	protected buildFixedParts(): string[] {
		return [];
	}

	/** --------------------------- Utilities ----------------------------– */
	/**
	 * Creates a map of sort options from an object.
	 * @param obj
	 * @returns
	 */
	protected createSortByMap(obj: Record<string, string>): SortMap {
		return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, value])) as SortMap;
	}
}
